import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_317e4ca9-d346-485a-9c59-d194eac572c3/artifacts/48kqarnp_image.png"
              alt="Allora Logo"
              className="h-20 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-500 mt-2">Last Updated: December 2025</p>
          </div>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-gray-600">
                Allora ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">2.1 Personal Information</h3>
              <p className="text-gray-600 mb-2">We collect information that you provide directly to us:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Name and email address</li>
                <li>Password (encrypted)</li>
                <li>Profile picture (optional)</li>
                <li>Gender (for avatar assignment)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">2.2 Health and Productivity Data</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Work tasks and documents you analyze</li>
                <li>Food items and nutritional data</li>
                <li>Health metrics (medicine logs, workout data, symptoms)</li>
                <li>Life management data (bills, appointments, expenses)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">2.3 Usage Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>AI feature usage and credit consumption</li>
                <li>Subscription and payment information</li>
                <li>Device information and browser type</li>
                <li>Log data and analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-gray-600 mb-2">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Provide, maintain, and improve our services</li>
                <li>Process AI-powered features (document analysis, food scanning)</li>
                <li>Manage your account and subscription</li>
                <li>Send you service-related notifications</li>
                <li>Respond to your requests and support needs</li>
                <li>Analyze usage patterns to improve the application</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. AI Processing and Data</h2>
              <p className="text-gray-600">
                When you use AI features (document analysis, food scanning, medical assistant), your data is processed by third-party AI services. We use industry-standard encryption and do not share your personal data with AI providers beyond what is necessary to provide the service. AI-generated insights are stored in your account and remain private.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Sharing and Disclosure</h2>
              <p className="text-gray-600 mb-2">We do not sell your personal information. We may share your information only in the following circumstances:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li><strong>Service Providers:</strong> Third-party services that help us operate (payment processors, AI providers, hosting services)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Security</h2>
              <p className="text-gray-600">
                We implement appropriate technical and organizational security measures to protect your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication</li>
              </ul>
              <p className="text-gray-600 mt-2">
                However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Data Retention</h2>
              <p className="text-gray-600">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. You may request deletion of your account and data at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your Rights and Choices</h2>
              <p className="text-gray-600 mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Access and review your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Delete your account and personal data</li>
                <li>Export your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p className="text-gray-600 mt-2">
                To exercise these rights, please contact us at privacy@allora.com
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Cookies and Tracking</h2>
              <p className="text-gray-600">
                We use cookies and similar tracking technologies to maintain your session, remember your preferences, and analyze usage patterns. You can control cookies through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Third-Party Services</h2>
              <p className="text-gray-600 mb-2">Our application integrates with third-party services:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li><strong>Authentication:</strong> Google OAuth for sign-in</li>
                <li><strong>Payments:</strong> Stripe for subscription processing</li>
                <li><strong>AI Services:</strong> OpenAI and other AI providers</li>
              </ul>
              <p className="text-gray-600 mt-2">
                These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Children's Privacy</h2>
              <p className="text-gray-600">
                Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. International Data Transfers</h2>
              <p className="text-gray-600">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Changes to This Privacy Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-gray-600 mt-2">
                Email: privacy@allora.com<br />
                Support: support@allora.com
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
